import {
  DefaultLandingPage,
  IProject,
  IProjectClosedInterval,
  IProjectInput,
  IWorkingHours,
  IWorkingScheduleDay,
  Maybe,
} from '~/client/graph'

import DateTimeFormat from '../enums/DateTimeFormat'
import { getScheduleWorkingDays } from '../utils/projectSchedule'
import { defaultTimezoneId } from '../utils/timezones'
import BaseModel from './BaseModel'

export const DEFAULT_WORK_START_HOUR = 6
export const DEFAULT_WORK_FINISH_HOUR = 18

export default class Project extends BaseModel<IProject> {
  public static none: Project

  public static fromDto(dto: IProject): Project {
    const project = new Project(dto.id)

    return Object.assign(project, dto, {
      dateTimeFormat: dto.dateTimeFormat || DateTimeFormat.Default,
      timezoneId: dto.timezoneId || defaultTimezoneId,
      workingDays: dto.workingDays || [],
    })
  }

  public static toInput(project: Project | IProject): IProjectInput {
    return {
      id: project.id || null,
      name: project.name,
      code: project.code,
      logoUrl: project.logoUrl,
      isLogoWhite: project.isLogoWhite,
      headerImageUrl: project.headerImageUrl,
      scheduleId: project.scheduleId || null,
      materialsUploadId: project.materialsUploadId || null,
      dateTimeFormat: project.dateTimeFormat,
      timezoneId: project.timezoneId,
      closedIntervals: project.closedIntervals,
      setUpFinished: project.setUpFinished,
      workingDays: project.workingDays,
      workingHours: project.workingHours,
      workingSchedule: project.workingSchedule,
      defaultLandingPage: project.defaultLandingPage,
      areWorkflowMarkupsDisabledByDefault:
        project.areWorkflowMarkupsDisabledByDefault,
    }
  }

  public id: string
  public code: string
  public logoUrl: string
  public isLogoWhite?: boolean
  public headerImageUrl: string
  public name: string
  public scheduleId: string
  public materialsUploadId: string
  public timezoneId: string
  public dateTimeFormat: DateTimeFormat = DateTimeFormat.Default
  public setUpFinished: string[]
  public closedIntervals?: IProjectClosedInterval[]
  public workingHours?: IWorkingHours
  public workingDays?: number[]
  /**
   * Per-day working windows (STX1-983), and the source of truth where present:
   * a weekday listed here is worked and carries its own hours. Absent on
   * projects saved before per-day hours existed — `buildProjectSchedule` falls
   * back to `workingDays` + `workingHours` for those.
   */
  /** Rows are nullable in the schema; read through them rather than destructure. */
  public workingSchedule?: Maybe<IWorkingScheduleDay>[]
  public defaultLandingPage?: DefaultLandingPage
  public areWorkflowMarkupsDisabledByDefault?: boolean

  public toInput(): IProjectInput {
    return Project.toInput(this)
  }

  public get isNone() {
    return this.isSameAs(Project.none)
  }

  public areIdsEquals(project: { id?: string }) {
    return this.id === project.id
  }

  /**
   * Which weekdays the project works, off `workingSchedule` — the source of
   * truth. The server sends the effective schedule, so a project that never
   * stored one still arrives with its legacy days expanded into rows, and
   * nothing here has to read `workingDays` (STX1-983).
   */
  public get projectWorkingDaysMap() {
    const map: { [key: string]: boolean } = {}

    getScheduleWorkingDays(this.workingSchedule).forEach(day => {
      map[day] = true
    })

    return map
  }
}

Project.none = Object.assign(new Project(), {
  name: '',
  code: '',
  logoUrl: '',
  isLogoWhite: null,
  headerImageUrl: '',
  scheduleId: '',
  addressId: null,
  timezoneId: '',
})
